import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { RolodexEntryJson } from 'types/rolodex/rolodexEntries'
import {
  parseFiniteNumber,
  parseNumber,
  parseString,
} from 'utils/parsingHelpers'

import { GeckoEntryBase } from './geckoEntry'

/** Default name of the thickness COPC file stored under the Rolodex entry. */
export const DEFAULT_COPC_FILE_NAME = 'cloud.copc.laz'

/**
 * Fuzzy-maps a raw units label (e.g. `mm`, `millimeter(s)`) to a
 * {@link GeckoMeasurementUnit}. Returns `undefined` when no label was provided
 * so callers can distinguish "units unspecified" from "inches"; when a label
 * IS present but unrecognized, assumes inches (UT thickness is usually inches).
 */
function measurementUnitFromLabel(
  label?: string
): GeckoMeasurementUnit | undefined {
  const normalized = label?.trim().toLowerCase()
  if (!normalized) {
    return undefined
  }
  if (
    normalized === 'mm' ||
    normalized === 'millimeter' ||
    normalized === 'millimeters'
  ) {
    return GeckoMeasurementUnit.Millimeters
  }
  return GeckoMeasurementUnit.Inches
}

export interface GeckoCopcPointCloudSettings {
  /** Rendered point size, in pixels. */
  point_size?: number
  /** Max points kept in memory during LOD streaming. */
  point_budget?: number
  /**
   * Screen-space-error threshold, in pixels: a node is refined into its
   * children once its projected on-screen size exceeds this. Lower = crisper
   * (refines sooner, more points on screen); higher = coarser/cheaper.
   */
  min_node_pixel_size?: number
}

export interface GeckoCopcSurfaceFitClip {
  axisPointM: [number, number, number]
  axisDirection: [number, number, number]
  seamDirection?: [number, number, number]
  radiusM: number
  halfLengthM: number
}

export interface GeckoCopcCoordinateReference {
  zeroStationM: number
  zeroAngleDeg: number
  axialDirection: 1 | -1
  circumferentialDirection: 1 | -1
}

function coordinateReference(
  value: unknown,
  fit?: GeckoCopcSurfaceFitClip
): GeckoCopcCoordinateReference | undefined {
  if (
    !fit?.seamDirection ||
    !value ||
    typeof value !== 'object' ||
    !('reference' in value) ||
    !('frame' in value)
  )
    return undefined
  const reference = value.reference
  const frame = value.frame
  if (
    !reference ||
    typeof reference !== 'object' ||
    !('zeroStationM' in reference) ||
    !('zeroAngleDeg' in reference) ||
    !('axialDirection' in reference) ||
    !('circumferentialDirection' in reference) ||
    !('sourceGeometryRevision' in reference) ||
    !frame ||
    typeof frame !== 'object' ||
    !('sourceGeometryRevision' in frame) ||
    frame.sourceGeometryRevision !== reference.sourceGeometryRevision
  )
    return undefined
  const zeroStationM = parseFiniteNumber(reference.zeroStationM)
  const zeroAngleDeg = parseFiniteNumber(reference.zeroAngleDeg)
  if (
    zeroStationM === undefined ||
    zeroAngleDeg === undefined ||
    zeroAngleDeg < 0 ||
    zeroAngleDeg >= 360 ||
    (reference.axialDirection !== 1 && reference.axialDirection !== -1) ||
    (reference.circumferentialDirection !== 1 &&
      reference.circumferentialDirection !== -1)
  )
    return undefined
  return {
    zeroStationM,
    zeroAngleDeg,
    axialDirection: reference.axialDirection,
    circumferentialDirection: reference.circumferentialDirection,
  }
}

function finiteVector3(value: unknown): [number, number, number] | undefined {
  if (
    !Array.isArray(value) ||
    value.length !== 3 ||
    !value.every((item) => typeof item === 'number' && Number.isFinite(item))
  ) {
    return undefined
  }
  return [value[0], value[1], value[2]]
}

function surfaceMatrix(value: unknown): number[] | undefined {
  if (
    !Array.isArray(value) ||
    value.length !== 16 ||
    !value.every((item) => typeof item === 'number' && Number.isFinite(item))
  ) {
    return undefined
  }
  return [...value]
}

function surfaceFitClip(value: unknown): GeckoCopcSurfaceFitClip | undefined {
  if (
    !value ||
    typeof value !== 'object' ||
    !('axisPointM' in value) ||
    !('axisDirection' in value) ||
    !('radiusM' in value) ||
    !('halfLengthM' in value)
  ) {
    return undefined
  }

  const axisPointM = finiteVector3(value.axisPointM)
  const axisDirection = finiteVector3(value.axisDirection)
  const radiusM = parseFiniteNumber(value.radiusM)
  const halfLengthM = parseFiniteNumber(value.halfLengthM)
  const axisLength = axisDirection && Math.hypot(...axisDirection)
  if (
    !axisPointM ||
    !axisDirection ||
    !axisLength ||
    !radiusM ||
    !halfLengthM ||
    radiusM <= 0 ||
    halfLengthM <= 0
  ) {
    return undefined
  }

  return {
    axisPointM,
    seamDirection: finiteVector3(
      'seamDirection' in value ? value.seamDirection : undefined
    ),
    axisDirection: [
      axisDirection[0] / axisLength,
      axisDirection[1] / axisLength,
      axisDirection[2] / axisLength,
    ],
    radiusM,
    halfLengthM,
  }
}

/**
 * A Rolodex entry describing Cloud-Optimized Point Cloud (COPC) files. Unlike
 * {@link GeckoPotreePointCloud} (a pre-tiled Potree octree served as many
 * files), a COPC entry points at one or two `.copc.laz` files streamed in-place
 * via HTTP range requests: UT thickness, plus an optional localization (lidar)
 * context cloud.
 */
export class GeckoCopcPointCloud extends GeckoEntryBase<
  {
    name?: string
    /** UT-thickness COPC file under this entry's Rolodex files. */
    thickness_file_name: string
    /**
     * Localization (lidar) COPC file under this entry's Rolodex files.
     * Omitted when the entry only has a thickness cloud.
     */
    localization_file_name?: string
    surface_file_name?: string
    surface_to_pointcloud_matrix?: number[]
    asset_fit_clip?: GeckoCopcSurfaceFitClip
    coordinate_reference?: GeckoCopcCoordinateReference
    /** Per-point scalar field to colorize by (defaults to `thickness`). */
    scalar_field?: string
    /**
     * Units for the scalar field, normalized from the raw `scalar_units` label
     * on the wire. `undefined` when the producer did not specify units.
     */
    scalar_measurement_unit?: GeckoMeasurementUnit
    /**
     * Optional linear display transform for the scalar: `value * scale + offset`.
     * e.g. set `scalar_scale` to 0.0393701 to show mm data in inches.
     */
    scalar_scale?: number
    scalar_offset?: number
    default_settings: GeckoCopcPointCloudSettings
  },
  {
    site: string
    organization: string
    unit?: string
    inspection?: string
    component?: string
  }
> {
  //----------------------------------------------------------------------------

  private constructor(json: RolodexEntryJson) {
    const settingsJson = (json.data?.default_settings ??
      {}) as GeckoCopcPointCloudSettings
    const fitClip = surfaceFitClip(json.data?.asset_fit_clip)

    super(
      json,
      {
        name: parseString(json.data?.name) ?? undefined,
        thickness_file_name:
          parseString(json.data?.thickness_file_name) ||
          parseString(json.data?.file_name) ||
          DEFAULT_COPC_FILE_NAME,
        localization_file_name:
          parseString(json.data?.localization_file_name) || undefined,
        surface_file_name:
          parseString(json.data?.surface_file_name) || undefined,
        surface_to_pointcloud_matrix: surfaceMatrix(
          json.data?.surface_to_pointcloud_matrix
        ),
        asset_fit_clip: fitClip,
        coordinate_reference: coordinateReference(
          json.data?.coordinate_reference,
          fitClip
        ),
        scalar_field: parseString(json.data?.scalar_field) ?? undefined,
        scalar_measurement_unit: measurementUnitFromLabel(
          parseString(json.data?.scalar_units)
        ),
        scalar_scale: parseNumber(json.data?.scalar_scale) ?? undefined,
        scalar_offset: parseNumber(json.data?.scalar_offset) ?? undefined,
        default_settings: {
          point_size: parseNumber(settingsJson?.point_size) ?? undefined,
          point_budget: parseNumber(settingsJson?.point_budget) ?? undefined,
          min_node_pixel_size:
            parseNumber(settingsJson?.min_node_pixel_size) ?? undefined,
        },
      },
      {
        organization: parseString(json.tags.organization) ?? '',
        site: parseString(json.tags.site) ?? '',
      },
      {}
    )
  }

  //----------------------------------------------------------------------------

  /** Builds a COPC point cloud from Rolodex JSON. */
  public static CreateFromJSON(json: RolodexEntryJson) {
    return new GeckoCopcPointCloud(json)
  }
}
